import Markdown from 'markdown-to-jsx'
import React from 'react'

import { VoucherIcon, RewardCardIcon } from '~/assets/icons'
import { AccordionItem } from '~/components/accordion'
import { generateCostsBreakdown } from '~/components/deal/utils/price-details'
import { Deal, DealProvider } from '~/gql/graphql'
import { useInBucket } from '~/hooks/use-in-bucket'

import { TitleSection, Sections } from './sections'
import styles from './styles.module.css'

const RewardContent = ({ provider, hasVoucher }: { provider: DealProvider; hasVoucher: boolean }) => {
  if (!hasVoucher) return null

  const rewardContent = [
    {
      providers: ['12774', '12175', '12165', '12193', '12833'], // ['Hyperoptic', 'Sky', 'TalkTalk', 'Vodafone', 'Shell Broadband']
      title: 'How to claim your voucher',
      body: [
        `Once your broadband is activated, you’ll get your voucher within 120 days. You then have 90 days to claim it.`,
        `Any questions? Email **hello@giftcloud.com** for help.`,
        `T&Cs apply.`
      ],
      icon: <VoucherIcon className={styles.iconShadow} />
    },
    {
      providers: ['12835'], // ['Community Fibre']
      title: 'How to claim your voucher',
      body: [
        `Once your broadband is activated, you’ll get your voucher within 90 days.`,
        `Any questions? Contact Community Fibre for help.`,
        `T&Cs apply.`
      ],
      icon: <VoucherIcon className={styles.iconShadow} />
    },
    {
      providers: ['12179'], // ['Virgin Media']
      title: 'How to claim your bill credit',
      body: [
        `You’ll get your credit or gift after your activation date.`,
        `Bill credit will be used on your first bill. Anything leftover will be applied to your following bills until it’s all used. T&Cs apply.`
      ],
      icon: null
    },
    {
      providers: ['12153'], // ['BT']
      title: 'How to claim your reward',
      body: [
        `Once your broadband is activated, you have 3 months to claim your reward card at www.bt.com/manage/bt-reward-card/.`,
        `T&Cs apply.`
      ],
      icon: <RewardCardIcon className={styles.iconShadow} />
    }
  ]

  const thisRewardContent = rewardContent.find((reward) => reward.providers.includes(provider.id))

  if (!thisRewardContent) return null

  return (
    <>
      <h6 className={styles.header} data-medium-margin>
        {thisRewardContent.title}
      </h6>

      <div className={styles.sections} data-has-border>
        <Markdown
          options={{
            overrides: {
              p: { props: { className: styles.sectionBody } },
              a: { props: { target: '_blank' } }
            }
          }}
        >
          {thisRewardContent.body.join('\n\n')}
        </Markdown>

        {thisRewardContent.icon && <div className={styles.sectionBody}>{thisRewardContent.icon}</div>}
      </div>
    </>
  )
}

const CostsContent = ({ deal }: { deal: Deal }) => {
  const isContractPricingVariant = useInBucket('experiment-broadband-asa-contract-pricing-extended', [
    'extreme',
    'lightweight'
  ])
  const priceRiseSmallPrint = deal.provider.contractTerms.priceRiseDetails

  const shownPromotions = ['voucher', 'bill-credit']
  const hasVoucher = deal.promotions.some((promotion) => shownPromotions.includes(promotion.type))

  const costsBreakdown = generateCostsBreakdown(deal)
  const {
    setupCosts: { setupCosts, setupCostTotal },
    monthlyCosts,
    averageMonthlyCost,
    totalContractCost
  } = costsBreakdown

  const averageMonthly = {
    title: 'Average monthly cost',
    amount: averageMonthlyCost || '£0.00',
    text: 'Total cost, including setup and monthly costs, divided by the number of months in your contract.'
  }

  const totalContract = {
    title: 'Total contract cost',
    amount: totalContractCost || '£0.00',
    text: 'Includes setup and monthly costs.'
  }

  const extraCosts = [
    {
      header: 'Mid contract price rise',
      body:
        isContractPricingVariant && !!priceRiseSmallPrint
          ? [priceRiseSmallPrint, "Check the provider's terms for details."]
          : [
              'Some providers may increase monthly costs during your contract, in line with inflation.',
              'Check their terms for details.'
            ]
    },
    {
      header: 'Out of contract price rise',
      body: [
        'It’s likely your monthly cost will increase when your contract term ends.',
        `That's why it’s always a good idea to switch or re-contract your broadband at the end of your contract.`
      ]
    },
    {
      header: 'Early exit fees',
      body: [
        'If you leave your contract early, you may have to pay an exit fee (or early termination charge).',
        'The amount depends on how long you have left on your contract and how much you pay each month.'
      ]
    }
  ]

  return (
    <>
      <div className={styles.sections} data-has-border>
        <TitleSection
          content={{
            title: 'Setup cost',
            amount: setupCostTotal
          }}
          boldAmount
          boldTitle
          noMargin
        />
        <AccordionItem isBottomAligned isOpen={false}>
          {setupCosts.map((item, index) => (
            <div className={styles.subHeaderContainer} key={index}>
              <p className={styles.subHeader}>{item.title}</p>
              <span>{item.amount}</span>
            </div>
          ))}
        </AccordionItem>
      </div>
      <TitleSection content={{ title: 'Monthly cost' }} boldTitle>
        {monthlyCosts.map((monthlyCost: Array<Array<string>>, index: number) => (
          <div className={styles.subHeaderContainer} key={index}>
            <p className={styles.subHeader}>{monthlyCost[0]}</p>
            <span className={styles.boldHeader}>{monthlyCost[1]}</span>
          </div>
        ))}
      </TitleSection>
      <TitleSection content={averageMonthly} boldAmount boldTitle border />
      <TitleSection content={totalContract} boldAmount boldTitle border />
      <RewardContent provider={deal.provider} hasVoucher={hasVoucher} />
      <h6 className={styles.header}>Extra costs to be aware of</h6>
      <Sections sections={extraCosts} headerExtraMargin largeBody />
    </>
  )
}

export default CostsContent
